import round from 'lodash/round';

import { BodilColor, NorlysColor } from '~src/branding';
import { useBranding } from '~src/context/BrandingContext';
import { useDisplayContext } from '~src/context/DisplayContext';

import { LegendEntry } from '../Legend/types';
import { DEFAULT_WEIGHTS } from './constants';
import { DesktopEnergyCostGraph, DesktopEnergyCostGraphNorlys } from './desktop';
import { MobileEnergyCostGraph, MobileEnergyCostGraphNorlys } from './mobile';
import { Container, ContainerNorlys } from './style';
import { EnergyCostGraphPropsCommon } from './types';

export const EnergyCostGraph = (props: EnergyCostGraphPropsCommon) => {
  const { isMobile } = useDisplayContext();
  const { darkColor, lightColor } = useBranding();

  const { annualPriceBefore, annualPriceAfter, variant = 'default', style } = props;

  const values = formatGraphValues(props);

  const isWide = variant === 'wide';

  const afterColor = isWide || isMobile ? darkColor : BodilColor.base.lavender;
  const beforeColor = isWide || isMobile ? lightColor : BodilColor.greyscale[30];

  const legendEntries = [
    {
      heading: 'Nuværende',
      value: round(annualPriceBefore, 0),
      iconColor: beforeColor,
    },
    {
      heading: 'Fremtidig',
      value: round(annualPriceAfter, 0),
      iconColor: afterColor,
    },
    {
      ...(isMobile && {
        heading: 'Årlig sparer du',
        value: round(annualPriceBefore - annualPriceAfter, 0),
      }),
    },
  ] as LegendEntry[];

  return (
    <Container style={style}>
      {isMobile && <MobileEnergyCostGraph {...{ values, legendEntries, beforeColor, afterColor, ...props }} />}
      {!isMobile && <DesktopEnergyCostGraph {...{ values, legendEntries, beforeColor, afterColor, ...props }} />}
    </Container>
  );
};

export const EnergyCostGraphNorlys = (props: EnergyCostGraphPropsCommon) => {
  const { isMobile } = useDisplayContext();

  const { annualPriceBefore, annualPriceAfter, style } = props;

  const values = formatGraphValues(props);

  const legendEntries = [
    {
      heading: 'Nuværende',
      value: round(annualPriceBefore, 0),
      iconColor: NorlysColor.secondary.lightPurple,
    },
    { heading: 'Fremtidig', value: round(annualPriceAfter, 0), iconColor: NorlysColor.secondary.purple },
    { heading: 'Årlig sparer du', value: annualPriceBefore - annualPriceAfter, valueColor: NorlysColor.primary.acid },
  ] as LegendEntry[];

  return (
    <>
      {isMobile && (
        <ContainerNorlys style={style}>
          <MobileEnergyCostGraphNorlys {...{ values, legendEntries, ...props }} />
        </ContainerNorlys>
      )}
      {!isMobile && <DesktopEnergyCostGraphNorlys {...{ values, legendEntries, ...props }} />}
    </>
  );
};

const formatGraphValues = ({
  weights = DEFAULT_WEIGHTS,
  annualPriceBefore,
  annualPriceAfter,
  maxDecimals = 0,
}: Pick<EnergyCostGraphPropsCommon, 'annualPriceBefore' | 'annualPriceAfter' | 'maxDecimals' | 'weights'>) =>
  weights.map(value => [round(annualPriceBefore * value, maxDecimals), round(annualPriceAfter * value, maxDecimals)]);
